/**
 * The order-tracking map (shop / rider / you pins + route line), shared by the classic order page and the Blinkit
 * one. Moved verbatim out of OrderTrackPage.jsx.
 */
import { useEffect } from "react";
import { MapContainer, TileLayer, Marker, Tooltip, Polyline, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Re-bind default marker assets (Leaflet's CRA bundler quirk).
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
    iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
    shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

// Coloured marker factory — different pins so the customer can tell the
// shop / rider / drop-off apart at a glance.
const pin = (color) => L.divIcon({
    className: "",
    iconSize: [28, 36],
    iconAnchor: [14, 34],
    popupAnchor: [0, -32],
    html: `<div style="position:relative;width:28px;height:36px;">
        <svg viewBox="0 0 28 36" width="28" height="36" xmlns="http://www.w3.org/2000/svg">
            <path d="M14 0C6.27 0 0 6.27 0 14c0 9.4 11.34 20.16 13.04 21.66a1.45 1.45 0 0 0 1.92 0C16.66 34.16 28 23.4 28 14 28 6.27 21.73 0 14 0Z" fill="${color}"/>
            <circle cx="14" cy="14" r="5" fill="#fff"/>
        </svg>
    </div>`,
});
const SHOP_PIN = pin("#0ea5e9");
const RIDER_PIN = pin("#f97316");
const CUST_PIN = pin("#16a34a");

function FitBounds({ points }) {
    const map = useMap();
    useEffect(() => {
        const valid = points.filter((p) => Number.isFinite(p?.[0]) && Number.isFinite(p?.[1]));
        if (valid.length === 0) return;
        if (valid.length === 1) {
            map.setView(valid[0], 15);
        } else {
            map.fitBounds(valid, { padding: [40, 40] });
        }
    }, [JSON.stringify(points)]);
    return null;
}

export default function OrderTrackMap({ track, points, hasMap }) {
    return (
        <>
        {hasMap && (
            <section className="mx-4 rounded-2xl overflow-hidden border border-border shadow-sm" data-testid="track-map-card">
                <div className="h-64 md:h-80 lg:h-96 w-full">
                    <MapContainer
                        center={points[0]}
                        zoom={14}
                        scrollWheelZoom={false}
                        style={{ height: "100%", width: "100%" }}
                        data-testid="track-map"
                    >
                        <TileLayer
                            attribution='&copy; OpenStreetMap'
                            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        />
                        <FitBounds points={points} />
                        {track?.shop?.lat && track?.shop?.lng && (
                            <Marker position={[track.shop.lat, track.shop.lng]} icon={SHOP_PIN}>
                                <Tooltip permanent direction="top" offset={[0, -28]}>Shop</Tooltip>
                            </Marker>
                        )}
                        {track?.rider?.lat && track?.rider?.lng && (
                            <Marker position={[track.rider.lat, track.rider.lng]} icon={RIDER_PIN}>
                                <Tooltip permanent direction="top" offset={[0, -28]}>Rider</Tooltip>
                            </Marker>
                        )}
                        {track?.customer?.lat && track?.customer?.lng && (
                            <Marker position={[track.customer.lat, track.customer.lng]} icon={CUST_PIN}>
                                <Tooltip permanent direction="top" offset={[0, -28]}>You</Tooltip>
                            </Marker>
                        )}
                        {points.length >= 2 && (
                            <Polyline positions={points} pathOptions={{ color: "#6366f1", weight: 3, dashArray: "6 6" }} />
                        )}
                    </MapContainer>
                </div>
                <div className="px-4 py-2 text-[11px] text-muted-foreground bg-muted/40 flex items-center gap-3 flex-wrap">
                    <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-sky-500" /> Shop</span>
                    {track?.rider && <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-orange-500" /> Rider</span>}
                    <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-600" /> You</span>
                    {track?.rider?.location_updated_at && (
                        <span className="ml-auto">Rider updated {timeAgo(track.rider.location_updated_at)}</span>
                    )}
                </div>
            </section>
        )}
        </>
    );
}

function timeAgo(ts) {
    if (!ts) return "";
    try {
        const d = (Date.now() - new Date(ts).getTime()) / 1000;
        if (d < 60) return `${Math.floor(d)}s ago`;
        if (d < 3600) return `${Math.floor(d / 60)}m ago`;
        return `${Math.floor(d / 3600)}h ago`;
    } catch { return ""; }
}
